import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import useAppStore from '@/stores/appStore';
import { getUserRole } from '@/lib/auth';
import { useLogout } from '@/hooks/useAuth';
import { useTickets } from '@/hooks/useTickets';
import { usersApi } from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  List, 
  X, 
  House, 
  Ticket, 
  UserCircle, 
  MapPin, 
  ChartLine, 
  Gear,
  Users,
  SignOut,
  Package,
  Eye,
  ArrowCounterClockwise,
  MagnifyingGlass,
  Bell,
  BellSlash,
  WhatsappLogo
} from '@phosphor-icons/react';
import { useNotificationSound } from '@/hooks/useNotificationSound';

const ROLE_LABELS = {
  super_admin: 'Admin',
  agent: 'Agent',
  foc: 'FOC',
  technician: 'Technician',
};

export const DashboardLayout = ({ children, role }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const sidebarOpen = useAppStore((state) => state.sidebarOpen);
  const toggleSidebar = useAppStore((state) => state.toggleSidebar);
  const currentUser = useAppStore((state) => state.currentUser);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const viewAsRole = useAppStore((state) => state.viewAsRole);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const setViewAsRole = useAppStore((state) => state.setViewAsRole);
  const setViewAsUser = useAppStore((state) => state.setViewAsUser);
  const clearViewAs = useAppStore((state) => state.clearViewAs);
  const logout = useLogout();
  const navigate = useNavigate();
  const { soundEnabled, toggleSound, playNotificationSound } = useNotificationSound();

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const effectiveRole = (isSuperAdmin && viewAsRole) ? viewAsRole : role;

  // User picker dialog state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState(null);
  const [pickerUsers, setPickerUsers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const openUserPicker = async (targetRole) => {
    setPickerRole(targetRole);
    setPickerOpen(true);
    setPickerLoading(true);
    setPickerSearch('');
    try {
      const { data } = await usersApi.list({ role: targetRole });
      setPickerUsers(data || []);
    } catch (e) {
      setPickerUsers([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setViewAsRole(pickerRole);
    setViewAsUser(user);
    setPickerOpen(false);
    
    // Redirect to the chosen view-as role's dashboard
    const paths = {
      customer: '/customer',
      agent: '/ops/agent',
      foc: '/ops/foc',
      technician: '/ops/tech',
      super_admin: '/ops/admin',
    };
    navigate(paths[pickerRole] || '/');
  };

  const handleExitViewAs = () => {
    clearViewAs();
    navigate('/ops/admin');
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    clearViewAs();
    const roleRoutes = {
      customer: '/customer',
      agent: '/ops/agent',
      foc: '/ops/foc',
      technician: '/ops/tech',
      super_admin: '/ops/admin',
    };
    const userRole = currentUser?.role || getUserRole() || role;
    const targetPath = roleRoutes[userRole] || '/';
    navigate(targetPath);
  };

  const filteredPickerUsers = pickerUsers.filter(u =>
    u.full_name?.toLowerCase().includes(pickerSearch.toLowerCase()) ||
    u.phone?.includes(pickerSearch)
  );

  const getNavItems = () => {
    switch (effectiveRole) {
      case 'customer':
        return [
          { icon: House, label: 'Dashboard', path: '/customer' },
          { icon: Ticket, label: 'My Tickets', path: '/customer/tickets' },
          { icon: Package, label: 'My Products', path: '/customer/products' },
        ];
      case 'agent':
        return [
          { icon: List, label: 'Ticket Queue', path: '/ops/agent' },
          { icon: Ticket, label: 'My Tickets', path: '/ops/agent/my-tickets' },
          { icon: UserCircle, label: 'Caller Lookup', path: '/ops/agent/lookup' },
        ];
      case 'foc':
        return [
          { icon: MapPin, label: 'Assignment Queue', path: '/ops/foc' },
          { icon: Ticket, label: 'All Tickets', path: '/ops/foc/tickets' },
        ];
      case 'super_admin':
        return [
          { icon: House, label: 'Dashboard', path: '/ops/admin' },
          { icon: Ticket, label: 'Tickets', path: '/ops/admin/tickets' },
          { icon: Users, label: 'Users', path: '/ops/admin/users' },
          { icon: Package, label: 'Product Catalog', path: '/ops/admin/products' },
          { icon: UserCircle, label: 'Caller Lookup', path: '/ops/agent/lookup' },
          { icon: WhatsappLogo, label: 'WhatsApp', path: '/ops/admin/whatsapp' },
          { icon: ChartLine, label: 'Analytics', path: '/ops/admin/analytics' },
          { icon: Gear, label: 'Configuration', path: '/ops/admin/config' },
        ];
      default:
        return [];
    }
  };

  const getDashboardPath = () => {
    switch (effectiveRole) {
      case 'customer': return '/customer';
      case 'agent': return '/ops/agent';
      case 'foc': return '/ops/foc';
      case 'technician': return '/ops/tech';
      case 'super_admin': return '/ops/admin';
      default: return '/';
    }
  };

  // Sound notification trigger on new tickets for Agent/Admin
  const isAgentView = effectiveRole === 'agent';
  const isAudibleView = isAgentView || effectiveRole === 'super_admin';
  const { data: queueTickets } = useTickets(
    { status: 'submitted' },
    { refetchInterval: 30000, enabled: isAudibleView }
  );
  const { data: allTickets } = useTickets(
    undefined,
    { refetchInterval: 30000, enabled: isAgentView }
  );

  const effectiveUserId = (isSuperAdmin && viewAsUser) ? viewAsUser.id : currentUser?.id;

  const agentBadges = useMemo(() => {
    if (!isAgentView) return {};
    const queueCount = queueTickets?.data?.length || 0;
    const myTickets = allTickets?.data?.filter(t => t.agent_id === effectiveUserId) || [];
    const unresolvedCount = myTickets.filter(t => ['picked_up', 'assigned', 'in_progress'].includes(t.status)).length;
    return {
      '/ops/agent': queueCount,
      '/ops/agent/my-tickets': unresolvedCount,
    };
  }, [isAgentView, queueTickets, allTickets, effectiveUserId]);

  const queueCount = queueTickets?.data?.length || 0;
  const prevQueueCountRef = useRef(queueCount);

  useEffect(() => {
    if (isAudibleView && queueCount > prevQueueCountRef.current) {
      playNotificationSound();
    }
    prevQueueCountRef.current = queueCount;
  }, [queueCount, isAudibleView, playNotificationSound]);

  const navItems = getNavItems();

  return (
    <div className="flex min-h-screen bg-muted/30 overflow-x-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-border transition-transform duration-200 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0'
        )}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border">
            <Link 
              to="#" 
              onClick={handleLogoClick}
              className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity"
            >
              <h1 className="text-2xl font-black tracking-tight text-slate-900 shrink-0">KWICC</h1>
              {tenantConfig?.logo_url && (
                <>
                  <div className="h-5 w-[1px] bg-slate-200 shrink-0" />
                  <img src={tenantConfig.logo_url} alt={tenantConfig.company_name} className="h-7 max-w-[80px] object-contain shrink" />
                </>
              )}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="lg:hidden"
            >
              <X size={24} />
            </Button>
          </div>

          {/* View As Role Switcher (Super Admin only) */}
          {isSuperAdmin && (
            <div className="px-4 pt-3 pb-1">
              {viewAsRole ? (
                <button
                  onClick={handleExitViewAs}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold hover:bg-amber-100 transition-colors"
                >
                  <ArrowCounterClockwise size={14} weight="bold" />
                  <div className="text-left flex-1 min-w-0">
                    <div>Viewing as {ROLE_LABELS[viewAsRole]}</div>
                    {viewAsUser && (
                      <div className="text-[10px] font-normal truncate">{viewAsUser.full_name}</div>
                    )}
                  </div>
                </button>
              ) : (
                <div className="space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1">View As</p>
                  <div className="flex gap-1">
                    {['agent', 'foc', 'technician'].map(r => (
                      <button
                        key={r}
                        onClick={() => openUserPicker(r)}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors"
                      >
                        <Eye size={12} />
                        {ROLE_LABELS[r]}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const badgeCount = agentBadges[item.path] || 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    queryClient.invalidateQueries();
                  }}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-all duration-200 relative',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  )}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  {item.label}
                  {badgeCount > 0 && (
                    <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-black rounded-full bg-red-500 text-white animate-pulse">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Sidebar footer/spacing spacer */}
          <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground select-none font-semibold">
            <span>KWICC © 2026</span>
            <span>v1.2.0</span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-3 sm:px-6 shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="lg:hidden shrink-0"
            >
              <List size={24} />
            </Button>
            
            {effectiveRole === 'customer' && (
              <Link
                to="#"
                onClick={handleLogoClick}
                className="flex items-center gap-2 mr-3 sm:mr-4 hover:opacity-80 transition-opacity lg:hidden"
              >
                {tenantConfig?.logo_url ? (
                  <img 
                    src={tenantConfig.logo_url} 
                    alt={tenantConfig.company_name || 'KWICC'} 
                    className="h-7 max-w-[120px] object-contain" 
                  />
                ) : (
                  <span className="text-lg font-black tracking-tight text-slate-900 uppercase">
                    {tenantConfig?.company_name || 'KWICC'}
                  </span>
                )}
              </Link>
            )}

            {isSuperAdmin && viewAsRole && (
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold uppercase tracking-wider truncate max-w-[180px] lg:max-w-xs">
                <Eye size={12} className="shrink-0" />
                <span className="truncate">As {ROLE_LABELS[viewAsRole]}{viewAsUser ? ` — ${viewAsUser.full_name}` : ''}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Sound toggle for agent/admin */}
            {(effectiveRole === 'agent' || effectiveRole === 'super_admin' || effectiveRole === 'foc') && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSound}
                className="h-9 w-9 text-slate-500 hover:text-slate-900 transition-colors"
                title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
                data-testid="sound-toggle-btn"
              >
                {soundEnabled ? <Bell size={20} weight="fill" /> : <BellSlash size={20} />}
              </Button>
            )}

            {currentUser && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none text-left">
                  <div className="text-right hidden sm:block max-w-[120px] lg:max-w-[160px]">
                    <p className="text-sm font-semibold text-slate-900 truncate">{currentUser.full_name}</p>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-full inline-block mt-0.5 truncate max-w-full">
                      {currentUser.role?.replace('_', ' ')}
                    </p>
                  </div>
                  <div 
                    className="h-9 w-9 rounded-full text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0"
                    style={{ backgroundColor: tenantConfig?.brand_colour || '#002FA7' }}
                  >
                    {currentUser.full_name?.charAt(0).toUpperCase()}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border border-border rounded-md shadow-md p-1 mt-1 z-50">
                <DropdownMenuItem asChild>
                  <Link 
                    to={currentUser.role === 'customer' ? '/customer/profile' : '/ops/profile'} 
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-slate-100 rounded-sm transition-colors cursor-pointer"
                  >
                    <UserCircle size={18} />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1 border-t border-slate-100" />
                <DropdownMenuItem 
                  onClick={logout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-red-50 hover:text-destructive rounded-sm transition-colors cursor-pointer focus:bg-red-50 focus:text-destructive"
                >
                  <SignOut size={18} />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto min-w-0">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* User Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Select {pickerRole ? ROLE_LABELS[pickerRole] : 'User'} to view as
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative mb-3">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or phone..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>

          {/* User List */}
          <div className="max-h-[300px] overflow-y-auto space-y-1.5">
            {pickerLoading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
              ))
            ) : filteredPickerUsers.length > 0 ? (
              filteredPickerUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left border border-transparent hover:border-slate-200"
                >
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                    {user.full_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{user.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user.phone}</p>
                  </div>
                </button>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No users found for this role
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};